import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Plus, Loader2, Pencil, Power } from "lucide-react";
import { getServiceIcon } from "../services/ui/iconMap";
import { getCategoryName } from "../services/utils/getCategoryName";
import { AddCategoryDialog } from "./AddCategoryDialog";
import { EditCategoryDialog } from "./EditCategoryDialog";

const API_URL = process.env.REACT_APP_API_URL || "";

export interface AdminCategory {
  id: string;
  key: string;
  name: string;
  nameEt: string | null;
  nameEn: string | null;
  nameRu: string | null;
  icon: string;
  sortOrder: number;
  isActive: boolean;
  parentId: string | null;
}

interface Props {
  onChange?: () => void;
}

export function ServiceCategoriesTab({ onChange }: Props) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCategory | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<{ success: boolean; data: AdminCategory[] }>(
        `${API_URL}/api/admin/service-categories?includeInactive=true`,
        { withCredentials: true }
      );
      if (res.data.success) setItems(res.data.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Top-level categories (for the parent picker).
  const topLevel = useMemo(
    () => items.filter((c) => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder),
    [items]
  );

  // Render order: each top-level row followed by its children.
  const ordered = useMemo(() => {
    const rows: { cat: AdminCategory; depth: number }[] = [];
    for (const parent of topLevel) {
      rows.push({ cat: parent, depth: 0 });
      items
        .filter((c) => c.parentId === parent.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach((child) => rows.push({ cat: child, depth: 1 }));
    }
    return rows;
  }, [items, topLevel]);

  const parentName = (parentId: string | null) => {
    const p = items.find((c) => c.id === parentId);
    return p ? getCategoryName(p, i18n.language, t) : "—";
  };

  const handleToggleActive = async (cat: AdminCategory) => {
    if (cat.isActive) {
      const hasChildren = items.some((c) => c.parentId === cat.id && c.isActive);
      const msg = hasChildren
        ? t("admin.categories.confirmDeactivateParent")
        : t("admin.categories.confirmDeactivate");
      if (!window.confirm(msg)) return;
      await axios.delete(`${API_URL}/api/admin/service-categories/${cat.id}`, { withCredentials: true });
    } else {
      await axios.patch(
        `${API_URL}/api/admin/service-categories/${cat.id}`,
        { isActive: true },
        { withCredentials: true }
      );
    }
    await fetchCategories();
    onChange?.();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <p className="text-sm text-muted-foreground">{t("admin.categories.description")}</p>
        <div className="flex-1" />
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t("admin.categories.addCategory")}
        </Button>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.categories.colName")}</TableHead>
              <TableHead>{t("admin.categories.colKey")}</TableHead>
              <TableHead>{t("admin.categories.colParent")}</TableHead>
              <TableHead>{t("admin.categories.colSortOrder")}</TableHead>
              <TableHead>{t("admin.categories.colStatus")}</TableHead>
              <TableHead className="text-right">{t("admin.categories.colActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            )}
            {!loading && ordered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  —
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              ordered.map(({ cat, depth }) => {
                const Icon = getServiceIcon(cat.icon);
                return (
                  <TableRow key={cat.id}>
                    <TableCell>
                      <div className={depth > 0 ? "flex items-center gap-2 pl-6" : "flex items-center gap-2"}>
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="font-medium">{getCategoryName(cat, i18n.language, t)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{cat.key}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{parentName(cat.parentId)}</TableCell>
                    <TableCell>{cat.sortOrder}</TableCell>
                    <TableCell>
                      <Badge variant={cat.isActive ? "default" : "secondary"}>
                        {cat.isActive
                          ? t("admin.categories.statusActive")
                          : t("admin.categories.statusInactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(cat)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(cat)}>
                        <Power className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      <AddCategoryDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        topLevel={topLevel}
        onSaved={() => {
          fetchCategories();
          onChange?.();
        }}
      />
      <EditCategoryDialog
        category={editing}
        topLevel={topLevel}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => {
          fetchCategories();
          onChange?.();
        }}
      />
    </div>
  );
}
